import { useAppSelector } from '@hooks/useRedux';
import { useRecordPayment } from '@hooks/accommodation';
import { selectSelectedBooking } from '@store/accommodation';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  ACCOMMODATION_PAYMENT_METHODS,
  AccommodationBooking,
} from '../../../../types/accommodation';
import { apiError } from '../../../../util/notify';

interface RecordPaymentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Optionally override the booking (defaults to selectedBooking from Redux) */
  booking?: AccommodationBooking | null;
  onClose: () => void;
  /** Called after a successful payment is recorded */
  onSuccess?: () => void;
}

export const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  isOpen,
  booking: bookingProp,
  onClose,
  onSuccess,
}) => {
  const selectedBooking = useAppSelector(selectSelectedBooking);
  const recordPayment = useRecordPayment();
  const isSubmitting = recordPayment.isPending;

  const booking = bookingProp ?? selectedBooking;

  const [amount, setAmount] = useState('');
  const [methodId, setMethodId] = useState<number>(1); // default: Cash
  const [notes, setNotes] = useState('');

  const amountRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const balance = booking
        ? Math.max(0, booking.total_amount - (booking.amount_paid ?? 0))
        : 0;
      setAmount(balance > 0 ? String(balance) : '');
      setMethodId(1);
      setNotes('');
      setTimeout(() => amountRef.current?.focus(), 80);
    }
  }, [isOpen, booking]);

  if (!isOpen || !booking) return null;

  const balance = Math.max(
    0,
    booking.total_amount - (booking.amount_paid ?? 0),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      apiError('Enter a valid payment amount.');
      return;
    }
    if (parsed > balance + 0.01) {
      apiError(
        `Payment exceeds balance due (${booking.currency} ${balance.toLocaleString()}).`,
      );
      return;
    }

    recordPayment.mutate(
      {
        booking_id: booking.id,
        amount: parsed,
        payment_method_id: methodId,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  return (
    /* Backdrop */
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Record payment
            </h3>
            <p className="text-xs text-gray-400">
              {booking.client
                ? `${booking.client.first_name} ${booking.client.last_name}`
                : `Booking #${booking.id.slice(0, 8).toUpperCase()}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={15} />
          </button>
        </div>

        {/* Balance summary */}
        <div className="mx-5 mt-4 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
          <span className="text-xs text-gray-500">Total amount</span>
          <span className="text-xs font-semibold text-gray-800">
            {booking.currency} {booking.total_amount.toLocaleString()}
          </span>
        </div>
        <div className="mx-5 mt-1 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
          <span className="text-xs text-gray-500">Already paid</span>
          <span className="text-xs font-semibold text-green-600">
            {booking.currency} {(booking.amount_paid ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="mx-5 mt-1 flex items-center justify-between rounded-xl bg-yellow-50 px-4 py-3">
          <span className="text-xs font-semibold text-yellow-700">
            Balance due
          </span>
          <span className="text-sm font-bold text-yellow-700">
            {booking.currency} {balance.toLocaleString()}
          </span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          {/* Amount */}
          <div>
            <label
              htmlFor="pm-amount"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Amount *
            </label>
            <input
              id="pm-amount"
              ref={amountRef}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              placeholder="0.00"
              required
            />
          </div>

          {/* Payment method */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-1.5 block text-xs font-medium text-gray-700">
              Payment method *
            </label>
            <div className="flex flex-wrap gap-2">
              {ACCOMMODATION_PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethodId(m.id)}
                  className={[
                    'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    methodId === m.id
                      ? 'border-pos-primary bg-pos-primary/10 text-pos-primary'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300',
                  ].join(' ')}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label
              htmlFor="pm-notes"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Notes (optional)
            </label>
            <input
              id="pm-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reference, receipt number…"
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-5 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : 'Record payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
